import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import DecisionBadge from './DecisionBadge';
import {
  Shield, CheckCircle2, Loader2, ArrowRight, ArrowLeft,
  Lightbulb, User, CalendarDays, Sparkles, Plus,
} from 'lucide-react';

const STEPS = [
  { label: 'Kies maatregel', icon: Lightbulb },
  { label: 'Impact & kosten', icon: Shield },
  { label: 'Eigenaar & planning', icon: User },
  { label: 'Bevestig', icon: CheckCircle2 },
];

const MeasureLinkWizard = ({ open, onClose, risk, onSave }) => {
  const { api } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    domain: 'process',
    impact: 3,
    complexity: 'medium',
    est_cost: '',
    owner: '',
    deadline: '',
    status: 'Gedefinieerd',
    priority: 'Midden',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && risk?.id) {
      setStep(0);
      setSelectedSuggestion(null);
      setIsCustom(false);
      setForm({ title: '', description: '', domain: 'process', impact: 3, complexity: 'medium', est_cost: '', owner: '', deadline: '', status: 'Gedefinieerd', priority: 'Midden' });
      fetchSuggestions();
    }
  }, [open, risk?.id]);

  const fetchSuggestions = async () => {
    if (!risk?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/ggrc/risks/${risk.id}/suggested-measures`);
      setSuggestions(res.data.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const selectSuggestion = (s) => {
    setSelectedSuggestion(s);
    setIsCustom(false);
    setForm(f => ({
      ...f,
      title: s.name,
      description: s.description,
      domain: s.domain || 'process',
      impact: s.impact || 3,
      complexity: s.complexity || 'medium',
      est_cost: s.est_cost || '',
    }));
  };

  const startCustom = () => {
    setSelectedSuggestion(null);
    setIsCustom(true);
    setForm(f => ({ ...f, title: '', description: '', domain: 'process', impact: 3, complexity: 'medium', est_cost: '' }));
  };

  const handleSave = async () => {
    if (!form.title?.trim()) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        linked_risk_id: risk.id,
      });
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const canNext = () => {
    if (step === 0) return selectedSuggestion || (isCustom && form.title?.trim());
    if (step === 1) return form.title?.trim();
    if (step === 2) return true;
    return true;
  };

  const impactLabels = { 1: 'Minimaal', 2: 'Laag', 3: 'Gemiddeld', 4: 'Hoog', 5: 'Zeer hoog' };
  const complexityLabels = { low: 'Eenvoudig', medium: 'Gemiddeld', high: 'Complex' };
  const complexityColors = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="measure-link-wizard">
        <DialogHeader className="pb-3 border-b border-neutral-100">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Shield className="h-4 w-4 text-neutral-600" strokeWidth={1.5} />
            Maatregel koppelen
          </DialogTitle>
          <DialogDescription className="text-xs">
            {risk?.title || 'Risico'}, stap {step + 1} van {STEPS.length}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-1 py-2">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={i} className="flex items-center gap-1 flex-1">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  isActive ? 'bg-black text-white' : isDone ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-400'
                }`}>
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> : <StepIcon className="h-3.5 w-3.5" strokeWidth={1.5} />}
                </div>
                <span className={`text-[10px] truncate hidden sm:block ${isActive ? 'font-semibold text-black' : 'text-neutral-400'}`}>{s.label}</span>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${isDone ? 'bg-emerald-300' : 'bg-neutral-200'}`} />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="py-3 min-h-[200px]">
          {/* Step 0: Choose measure */}
          {step === 0 && (
            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
              ) : (
                <>
                  <p className="text-xs text-neutral-500 mb-2">
                    <Sparkles className="h-3 w-3 inline mr-1 text-amber-500" />
                    Aanbevelingen op basis van risico-categorie en ernst
                  </p>
                  {suggestions.filter(s => !s.already_exists).map((s, i) => (
                    <button
                      key={i}
                      onClick={() => selectSuggestion(s)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        selectedSuggestion?.name === s.name
                          ? 'border-black bg-neutral-50 ring-1 ring-black'
                          : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                      }`}
                      data-testid={`suggestion-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                          selectedSuggestion?.name === s.name ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-500'
                        }`}>
                          <Shield className="h-4 w-4" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black">{s.name}</p>
                          <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{s.description}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge className={`text-[10px] ${complexityColors[s.complexity] || 'bg-neutral-100 text-neutral-600'}`}>{complexityLabels[s.complexity]}</Badge>
                            <span className="text-[10px] text-neutral-400">Impact: {impactLabels[s.impact]}</span>
                            {s.est_cost && <span className="text-[10px] text-neutral-400">{s.est_cost}</span>}
                          </div>
                        </div>
                        {selectedSuggestion?.name === s.name && (
                          <CheckCircle2 className="h-5 w-5 text-black shrink-0" strokeWidth={2} />
                        )}
                      </div>
                    </button>
                  ))}

                  {suggestions.some(s => s.already_exists) && (
                    <div className="pt-2">
                      <p className="text-[10px] text-neutral-400 uppercase tracking-wider mb-1.5">Al aanwezig in je organisatie</p>
                      {suggestions.filter(s => s.already_exists).map((s, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-neutral-50 mb-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" strokeWidth={1.5} />
                          <span className="text-xs text-neutral-600">{s.name}</span>
                          <DecisionBadge type="auto" />
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={startCustom}
                    className={`w-full text-left p-3 rounded-xl border-2 border-dashed transition-all ${
                      isCustom ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                    data-testid="custom-measure-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-neutral-100 flex items-center justify-center">
                        <Plus className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">Eigen maatregel definiëren</p>
                        <p className="text-xs text-neutral-400">Geen passende suggestie? Maak je eigen maatregel.</p>
                      </div>
                    </div>
                  </button>
                  {isCustom && (
                    <div className="space-y-3 p-3 bg-neutral-50 rounded-xl">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Naam *</Label>
                        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="bijv. Firewall upgrade" data-testid="custom-measure-title" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Beschrijving</Label>
                        <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="text-sm" placeholder="Korte beschrijving..." data-testid="custom-measure-description" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 1: Impact & cost */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                <p className="text-sm font-medium text-black">{form.title}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{form.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Impact op risico (1-5)</Label>
                  <Select value={String(form.impact)} onValueChange={v => setForm(f => ({ ...f, impact: parseInt(v) }))}>
                    <SelectTrigger data-testid="wizard-impact"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(v => <SelectItem key={v} value={String(v)}>{v}: {impactLabels[v]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Complexiteit</Label>
                  <Select value={form.complexity} onValueChange={v => setForm(f => ({ ...f, complexity: v }))}>
                    <SelectTrigger data-testid="wizard-complexity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(complexityLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Geschatte kosten</Label>
                <Input value={form.est_cost} onChange={e => setForm(f => ({ ...f, est_cost: e.target.value }))} placeholder="bijv. 2000-5000" data-testid="wizard-cost" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Domein</Label>
                <Select value={form.domain} onValueChange={v => setForm(f => ({ ...f, domain: v }))}>
                  <SelectTrigger data-testid="wizard-domain"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="people">Mensen</SelectItem>
                    <SelectItem value="process">Processen</SelectItem>
                    <SelectItem value="technology">Technologie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Owner & deadline */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Eigenaar</Label>
                <Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="bijv. CISO, IT Manager" data-testid="wizard-owner" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Deadline</Label>
                <Input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} data-testid="wizard-deadline" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="wizard-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gedefinieerd">Gedefinieerd</SelectItem>
                      <SelectItem value="Geïmplementeerd">Geïmplementeerd</SelectItem>
                      <SelectItem value="Operationeel">Operationeel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Prioriteit</Label>
                  <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="wizard-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hoog">Hoog</SelectItem>
                      <SelectItem value="Midden">Midden</SelectItem>
                      <SelectItem value="Laag">Laag</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-xs text-neutral-500 mb-2">Controleer de samenvatting en bevestig.</p>
              <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100">
                {[
                  { label: 'Maatregel', value: form.title },
                  { label: 'Beschrijving', value: form.description || '-' },
                  { label: 'Impact', value: impactLabels[form.impact] || form.impact },
                  { label: 'Complexiteit', value: complexityLabels[form.complexity] },
                  { label: 'Geschatte kosten', value: form.est_cost || '-' },
                  { label: 'Eigenaar', value: form.owner || '-' },
                  { label: 'Deadline', value: form.deadline || '-' },
                  { label: 'Gekoppeld risico', value: risk?.title || '-' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-xs text-neutral-500">{item.label}</span>
                    <span className="text-xs font-medium text-black text-right max-w-[60%] truncate">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-neutral-100 flex items-center justify-between">
          <div>
            {step > 0 && (
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setStep(s => s - 1)} data-testid="wizard-prev">
                <ArrowLeft className="h-3 w-3 mr-1" strokeWidth={1.5} />Vorige
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>Annuleren</Button>
            {step < STEPS.length - 1 ? (
              <Button size="sm" className="bg-black text-white text-xs hover:bg-neutral-800" onClick={() => setStep(s => s + 1)} disabled={!canNext()} data-testid="wizard-next">
                Volgende<ArrowRight className="h-3 w-3 ml-1" strokeWidth={1.5} />
              </Button>
            ) : (
              <Button size="sm" className="bg-black text-white text-xs hover:bg-neutral-800" onClick={handleSave} disabled={saving || !form.title?.trim()} data-testid="wizard-confirm">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" strokeWidth={1.5} />Bevestigen & opslaan</>}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MeasureLinkWizard;
